import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaCheck, FaUsers, FaInfoCircle, FaEllipsisV } from 'react-icons/fa';
import { MdSync } from 'react-icons/md';
import { getFileMeta } from './fileTypes';
import DriveItemMenu from './DriveItemMenu';
import { useFolderColor } from './folderColorStore';
import { getSize } from 'helpers/utils';

const COLOR_TINTS = {
  folder:  '#eff6ff', image:   '#f0f9ff', pdf:     '#fef2f2',
  word:    '#eff6ff', excel:   '#f0fdf4', ppt:     '#fff7ed',
  video:   '#f5f3ff', audio:   '#fdf2f8', archive: '#fff7ed',
  code:    '#eef2ff', text:    '#f1f5f9', other:   '#f1f5f9',
};

const DriveGridItem = ({ item, index, isSelected, onToggleSelect, onOpen, handleAction }) => {
  const { kind, icon: Icon, color: defaultColor } = getFileMeta(item);
  const isFolder = kind === 'folder';
  const isImage  = kind === 'image';
  const folderColor = useFolderColor(isFolder ? item.id : null, isFolder ? index : undefined);
  const useFolderTint = isFolder && folderColor;
  const color = useFolderTint ? folderColor.icon : defaultColor;
  const tint  = useFolderTint ? folderColor.tint : (COLOR_TINTS[kind] || COLOR_TINTS.other);
  const [imgError, setImgError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuBtnRef = useRef(null);

  const handleCardClick = (e) => {
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      onToggleSelect(item);
      return;
    }
    onOpen(item);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setMenuOpen(true);
  };

  const showThumb = isImage && item.preview_url && !imgError;

  return (
    <div
      onClick={handleCardClick}
      onContextMenu={handleContextMenu}
      className="tw-group tw-relative tw-bg-white tw-rounded-lg tw-overflow-hidden tw-cursor-pointer tw-select-none tw-transition-all tw-duration-200 hover:tw-shadow-lg hover:-tw-translate-y-0.5"
      style={{
        border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
        boxShadow: isSelected ? '0 0 0 3px rgba(37,99,235,0.15)' : 'none',
      }}
    >
      {/* Thumbnail area */}
      <div
        className="tw-relative tw-w-full tw-flex tw-items-center tw-justify-center tw-overflow-hidden"
        style={{ aspectRatio: '16 / 10', backgroundColor: tint, borderBottom: '1px solid #f1f5f9' }}
      >
        {showThumb ? (
          <img src={item.preview_url} alt={item.name} className="tw-w-full tw-h-full tw-object-cover" loading="lazy" onError={() => setImgError(true)} />
        ) : (
          <Icon size={48} style={{ color, opacity: 0.9 }} />
        )}

        {/* TOP-LEFT: checkbox only */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(item); }}
          aria-label={isSelected ? 'Deselect' : 'Select'}
          className={isSelected ? 'tw-opacity-100' : 'tw-opacity-0 group-hover:tw-opacity-100'}
          style={{
            appearance: 'none', WebkitAppearance: 'none',
            position: 'absolute', top: 8, left: 8, zIndex: 5,
            width: 22, height: 22, padding: 0, margin: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: isSelected ? '#2563eb' : 'rgba(255,255,255,0.95)',
            color: isSelected ? '#ffffff' : 'transparent',
            border: `1px solid ${isSelected ? '#2563eb' : '#cbd5e1'}`,
            borderRadius: 6, cursor: 'pointer',
            transition: 'opacity 150ms ease, background-color 150ms ease',
            boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
          }}
        >
          {isSelected && <FaCheck size={10} />}
        </button>

        {/* TOP-RIGHT: three-dot menu — visible on all viewports */}
        <button
          ref={menuBtnRef}
          type="button"
          onClick={(e) => { e.stopPropagation(); setMenuOpen(true); }}
          aria-label="More actions"
          style={{
            appearance: 'none', WebkitAppearance: 'none',
            position: 'absolute', top: 6, right: 6, zIndex: 5,
            width: 24, height: 24, padding: 0, margin: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.95)',
            color: '#475569',
            border: '1px solid #e2e8f0',
            borderRadius: 6, cursor: 'pointer',
            boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
            transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#0f172a';
            e.currentTarget.style.color = '#ffffff';
            e.currentTarget.style.borderColor = '#0f172a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.95)';
            e.currentTarget.style.color = '#475569';
            e.currentTarget.style.borderColor = '#e2e8f0';
          }}
        >
          <FaEllipsisV size={11} />
        </button>
      </div>

      {/* Meta — title / size · date / [Shared] [i Details] */}
      <div style={{ padding: '10px 14px 12px' }}>
        <p
          className="tw-truncate tw-m-0"
          style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}
          title={item.name}
        >
          {item.name}
        </p>
        <p
          className="tw-truncate tw-m-0"
          style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8', marginTop: 2, lineHeight: 1.4 }}
        >
          {isFolder ? 'Folder' : getSize(item.file_size || 0)}
          {item.updated_date && <> · {item.updated_date}</>}
        </p>

        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            marginTop: 8, minWidth: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              {item.public && (
                <span
                  title="Shared"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px',
                    backgroundColor: '#eff6ff',
                    color: '#2563eb',
                    border: '1px solid #dbeafe',
                    borderRadius: 999,
                    fontSize: 10, fontWeight: 700, lineHeight: 1.4,
                  }}
                >
                  <FaUsers size={9} />
                  <span>Shared</span>
                </span>
              )}
              {item.synced_from_opla && (
                <span
                  title="Synced from Opla CRM"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '2px 8px',
                    backgroundColor: '#f0fdfa',
                    color: '#0d9488',
                    border: '1px solid #99f6e4',
                    borderRadius: 999,
                    fontSize: 10, fontWeight: 700, lineHeight: 1.4,
                  }}
                >
                  <MdSync size={10} />
                  <span>Opla</span>
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleAction(item, 'detail')}
            aria-label="Details"
            title="Details"
            style={{
              appearance: 'none', WebkitAppearance: 'none', boxShadow: 'none',
              width: 22, height: 22, padding: 0, margin: 0, flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: '#f8fafc',
              color: '#64748b',
              border: '1px solid #e2e8f0',
              borderRadius: 6, cursor: 'pointer',
              transition: 'background-color 120ms ease, color 120ms ease, border-color 120ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0f172a';
              e.currentTarget.style.color = '#ffffff';
              e.currentTarget.style.borderColor = '#0f172a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f8fafc';
              e.currentTarget.style.color = '#64748b';
              e.currentTarget.style.borderColor = '#e2e8f0';
            }}
          >
            <FaInfoCircle size={10} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <DriveItemMenu
          item={item}
          anchorRef={menuBtnRef}
          onClose={() => setMenuOpen(false)}
          handleAction={handleAction}
        />
      )}
    </div>
  );
};

DriveGridItem.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number,
  isSelected: PropTypes.bool.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  handleAction: PropTypes.func.isRequired,
};

export default DriveGridItem;
